/**
 * The Recurring card's history panel, second half (SPEC.md §7.1.3, §8.7, DECISIONS.md
 * D140, D145).
 *
 * `DeltaHistogram` (rendered alongside this) answers "what's the shape of how this usually
 * goes" — this answers "what actually happened, and when." Capped at the most recent 10
 * entries server-side (`recent_occurrences`) rather than the task's full lifetime; see
 * `task_history`'s own docstring for why shipping everything on every open was real,
 * avoidable cost.
 *
 * The single most recent *resolved* entry — done, skipped, or missed, whichever it is —
 * gets a Fix control, correctable to any of the three. Nothing else does, including an
 * *older* resolved row once a newer outcome exists: the server would refuse reclaiming
 * that one anyway (only the latest outcome is eligible, same reasoning `reopen_occurrence`
 * already uses, DECISIONS.md D90), so the button is simply never offered rather than
 * offered and left to fail. That's a real constraint, not a UI choice — occurrences chain
 * sequentially (each one's next due date is computed from whatever resolved before it), so
 * correcting one in the middle would mean unwinding and redoing everything after it.
 */

import { useState } from "react";
import { ApiError, api, type Occurrence, type TaskHistory } from "../api";
import { Check, Cross, SkipArc, Wrench } from "./icons";

type Outcome = "completed" | "skipped" | "missed";

function outcomeLabel(occ: Occurrence): string {
  switch (occ.status) {
    case "completed":
      if (occ.delta_days === 0) return "done, on time";
      if (occ.delta_days !== null && occ.delta_days > 0) return `done, ${occ.delta_days}d early`;
      if (occ.delta_days !== null) return `done, ${-occ.delta_days}d late`;
      return "done";
    case "skipped":
      return occ.note ? `skipped - ${occ.note}` : "skipped";
    case "missed":
      return "missed";
    default:
      return "due";
  }
}

export function RecentOccurrences({
  taskId,
  history,
  onFixed,
}: {
  taskId: string;
  history: TaskHistory;
  /** Refetch both the history panel and the card's own list — a reclaimed occurrence
   * changes the rate and streak badges shown outside this panel too. */
  onFixed: () => void | Promise<void>;
}) {
  const [fixTarget, setFixTarget] = useState<string | null>(null);
  const [fixDate, setFixDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The single occurrence D140/D145's reclaim can act on: the most recent entry that
  // isn't still pending, whatever its current outcome. Nothing to fix if there's no
  // history yet, or the only entry is the live pending one.
  const mostRecentResolved = history.recent_occurrences.find((o) => o.status !== "pending");
  const fixable = mostRecentResolved?.due_date ?? null;

  function startFix(dueDate: string) {
    setFixTarget(dueDate);
    setFixDate(dueDate);
    setError(null);
  }

  async function fix(outcome: Outcome) {
    if (!fixTarget) return;
    setBusy(true);
    setError(null);
    try {
      if (outcome === "completed") {
        await api.completeOccurrence(taskId, fixTarget, undefined, fixDate || fixTarget);
      } else if (outcome === "skipped") {
        await api.skipOccurrence(taskId, fixTarget);
      } else {
        await api.missOccurrence(taskId, fixTarget);
      }
      setFixTarget(null);
      await onFixed();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not fix that.");
    } finally {
      setBusy(false);
    }
  }

  if (history.recent_occurrences.length === 0) {
    return <p className="small muted">Nothing yet.</p>;
  }

  return (
    <>
      <ul className="recent-occurrences">
        {history.recent_occurrences.map((occ) => (
          <li
            key={occ.due_date}
            // The form itself renders below the whole list, not inline under this one
            // row — reported directly: with nothing marking which row it belongs to,
            // it read as detached from the thing it's editing.
            className={`recent-occurrence${fixTarget === occ.due_date ? " editing" : ""}`}
          >
            <span className="recent-occurrence-date small">{occ.due_date}</span>
            <span className="recent-occurrence-status small muted">{outcomeLabel(occ)}</span>
            {occ.due_date === fixable && fixTarget !== occ.due_date && (
              <button
                type="button"
                className="btn ghost small"
                onClick={() => startFix(occ.due_date)}
                aria-label={`Fix: correct what happened on ${occ.due_date}`}
                title="Recorded wrong, or not at all - correct it, dated when it actually happened"
              >
                <Wrench size={14} />
              </button>
            )}
          </li>
        ))}
      </ul>

      {fixTarget && (
        <div className="recent-occurrence-fix">
          <div className="row wrap">
            <label className="small muted">
              Mark on{" "}
              <input
                type="date"
                className="input field"
                value={fixDate}
                onChange={(e) => setFixDate(e.target.value)}
                aria-label="Actual completion date"
              />
            </label>
            <span className="recent-occurrence-fix-outcomes" role="group" aria-label="Correct to">
              <button
                type="button"
                className="btn ghost small"
                disabled={busy}
                onClick={() => void fix("completed")}
                aria-label="Mark done"
                title="Done"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                className="btn ghost small"
                disabled={busy}
                onClick={() => void fix("skipped")}
                aria-label="Mark skipped"
                title="Skipped"
              >
                <SkipArc size={14} />
              </button>
              <button
                type="button"
                className="btn ghost small"
                disabled={busy}
                onClick={() => void fix("missed")}
                aria-label="Mark missed"
                title="Missed"
              >
                <Cross size={14} />
              </button>
            </span>
            {/* The safe choice carries the visual weight, not the three that write
                something - this is a confirm dialog, not a form where the "next step"
                deserves the emphasis. */}
            <button
              type="button"
              className="btn primary small"
              disabled={busy}
              onClick={() => setFixTarget(null)}
            >
              Cancel
            </button>
          </div>
          {error && (
            <p className="error small" style={{ margin: "0.35rem 0 0" }}>
              {error}
            </p>
          )}
        </div>
      )}
    </>
  );
}
